import { c as _c } from "react/compiler-runtime";
import { useRegistrationsControllerGetCompanyRoles } from "@dc/gateway-jsonapi";
import { addToast } from "@dc/ui/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { getPublicTrialRegistrationErrorMessage, getPublicTrialRegistrationErrorTarget } from "../../publicTrialRegistrationError";
import { createRegisterTrialPublicFormSchema, type RegisterTrialPublicFormInput, type RegisterTrialPublicFormValues } from "../../RegisterTrialPublicForm.schema";
import type { RegisterTrialPublicController } from "../../register-public.types";
import { SUPPORT_EMAIL } from "../../TrialSetupReportedNotice";
import { readTrialPrefill, type TrialPrefill } from "../../trialPrefill";
export function useRegisterTrialPublicForm(controller, identityPrefill) {
  const $ = _c(37);
  const t = useTranslations("trials.register_public");
  const [submissionTurnstileError, setSubmissionTurnstileError] = useState(null);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      query: {
        select: _temp
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: t1
  } = useRegistrationsControllerGetCompanyRoles(t0);
  let t2;
  if ($[1] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const companyRoles = t2;
  let t3;
  if ($[3] !== searchParams) {
    t3 = readTrialPrefill(searchParams);
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== identityPrefill || $[6] !== t3) {
    t4 = {
      ...t3,
      ...identityPrefill
    };
    $[5] = identityPrefill;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const prefill = t4;
  let t5;
  if ($[8] !== t) {
    t5 = zodResolver(createRegisterTrialPublicFormSchema({
      firstNameRequired: t("validation.first_name_required"),
      lastNameRequired: t("validation.last_name_required"),
      emailRequired: t("validation.email_required"),
      emailInvalid: t("validation.email_invalid"),
      personalEmail: domain => t("validation.personal_email", {
        domain
      }),
      companyRequired: t("validation.company_required"),
      companyTooLong: t("validation.company_too_long"),
      phoneRequired: t("validation.phone_required"),
      phoneTooLong: t("validation.phone_too_long"),
      countryRequired: t("validation.country_required"),
      stateRequired: t("validation.state_required"),
      termsRequired: t("validation.terms_required"),
      customRoleRequired: t("validation.custom_role_required"),
      customRoleTooLong: t("validation.custom_role_too_long")
    }));
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = prefill.firstName ?? "";
  const t7 = prefill.lastName ?? "";
  const t8 = prefill.email ?? "";
  const t9 = prefill.company ?? "";
  let t10;
  if ($[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = {
      firstName: t6,
      lastName: t7,
      email: t8,
      company: t9,
      companyRole: undefined,
      phone: "",
      country: "",
      state: "",
      marketingOptIn: false,
      password: "",
      confirmPassword: "",
      acceptedTerms: false
    };
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t5) {
    t11 = {
      resolver: t5,
      defaultValues: t10
    };
    $[15] = t10;
    $[16] = t5;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const form = useForm(t11);
  let t12;
  if ($[18] !== controller || $[19] !== form || $[20] !== t) {
    t12 = async values => {
      form.clearErrors(["company", "email"]);
      setSubmissionTurnstileError(null);
      ;
      try {
        await controller.submit(values);
      } catch (t13) {
        const error = t13;
        const message = getPublicTrialRegistrationErrorMessage(error, {
          conflict: t("errors.conflict", {
            email: SUPPORT_EMAIL
          }),
          turnstile: t("errors.turnstile"),
          validation: t("errors.validation"),
          rateLimited: t("errors.rate_limited"),
          unavailable: t("errors.unavailable"),
          unknown: t("errors.unknown", {
            email: SUPPORT_EMAIL
          })
        });
        const target = getPublicTrialRegistrationErrorTarget(error);
        if (target === "turnstile") {
          setSubmissionTurnstileError(message);
          return;
        }
        addToast({
          title: t("errors.toast_title"),
          description: message,
          color: "danger"
        });
      }
    };
    $[18] = controller;
    $[19] = form;
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const onSubmit = t12;
  const t13 = controller.turnstile.error ?? submissionTurnstileError;
  let t14;
  let t15;
  let t16;
  if ($[22] !== controller.turnstile) {
    t14 = token => {
      setSubmissionTurnstileError(null);
      controller.turnstile.onSuccess(token);
    };
    t15 = () => {
      setSubmissionTurnstileError(null);
      controller.turnstile.onExpire();
    };
    t16 = () => {
      setSubmissionTurnstileError(null);
      controller.turnstile.onError();
    };
    $[22] = controller.turnstile;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
  }
  let t17;
  if ($[26] !== controller.turnstile || $[27] !== t13 || $[28] !== t14 || $[29] !== t15 || $[30] !== t16) {
    t17 = {
      ...controller.turnstile,
      error: t13,
      onSuccess: t14,
      onExpire: t15,
      onError: t16
    };
    $[26] = controller.turnstile;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const turnstile = t17;
  let t18;
  if ($[32] !== companyRoles || $[33] !== form || $[34] !== onSubmit || $[35] !== turnstile) {
    t18 = {
      form,
      companyRoles,
      onSubmit,
      turnstile
    };
    $[32] = companyRoles;
    $[33] = form;
    $[34] = onSubmit;
    $[35] = turnstile;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
}
function _temp(data) {
  return data.data.attributes.roles;
}